import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Divider,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Switch,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  RestartAlt as RestartAltIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { formatCurrency } from "../../utils/formatters";

/**
 * Product Filters component
 * Displays filters for product catalog
 *
 * @param {Object} props - Component props
 * @param {Object} props.categoryHierarchy - Category hierarchy data
 * @param {Array} props.categories - All categories
 * @param {string} props.selectedCategory - Selected category ID
 * @param {string} props.selectedSubcategory - Selected subcategory ID
 * @param {Function} props.handleCategoryChange - Function to handle category change
 * @param {Function} props.handleSubcategoryChange - Function to handle subcategory change
 * @param {Array} props.priceRange - Selected price range
 * @param {Array} props.minMaxPrice - Min and max price values
 * @param {Function} props.handlePriceRangeChange - Function to handle price range change
 * @param {Array} props.availableAttributes - Available attributes for filtering
 * @param {Object} props.attributeFilters - Current attribute filter values
 * @param {Function} props.handleAttributeFilterChange - Function to handle attribute filter change
 * @param {Function} props.clearFilters - Function to clear all filters
 * @param {boolean} props.loadingAttributes - Whether attributes are loading
 * @param {Function} props.setUserSetPriceRange - Function to set userSetPriceRange
 * @param {Function} props.setPriceRange - Function to set the price range directly
 * @returns {JSX.Element} The ProductFilters component
 */
const ProductFilters = ({
  categoryHierarchy,
  categories,
  selectedCategory,
  selectedSubcategory,
  handleCategoryChange,
  handleSubcategoryChange,
  priceRange,
  minMaxPrice,
  handlePriceRangeChange,
  weightRange,
  minMaxWeight,
  handleWeightRangeChange,
  setUserSetWeightRange,
  setWeightRange,
  availableAttributes,
  attributeFilters,
  handleAttributeFilterChange,
  clearFilters,
  loadingAttributes,
  setUserSetPriceRange,
  setPriceRange,
}) => {
  const { t } = useTranslation("common");
  const { t: tProducts } = useTranslation("products");

  // Ensure we have valid objects and arrays to prevent errors
  const safeAttributeFilters = attributeFilters || {};
  const safeCategoryHierarchy = categoryHierarchy || {};
  const safeCategories = categories || [];
  const safeAvailableAttributes = availableAttributes || [];

  // Get subcategories for the selected category
  const subcategories = selectedCategory
    ? safeCategories.filter((cat) => cat.parentId === selectedCategory)
    : [];

  const [localPriceRange, setLocalPriceRange] = useState(priceRange);
  const [localWeightRange, setLocalWeightRange] = useState(weightRange || [0, 0]);

  useEffect(() => {
    setLocalPriceRange(priceRange);
  }, [priceRange]);

  useEffect(() => {
    if (weightRange) {
      setLocalWeightRange(weightRange);
    }
  }, [weightRange]);

  const commitPriceRange = (newRange) => {
    let [min, max] = newRange;
    min = Number(min);
    max = Number(max);
    if (Number.isNaN(min) || Number.isNaN(max)) return;
    min = Math.max(min, minMaxPrice[0] || 0);
    max = Math.min(max, minMaxPrice[1] || 10000);
    if (min > max) [min, max] = [max, min];
    const clamped = [min, max];
    setLocalPriceRange(clamped);
    handlePriceRangeChange(null, clamped);
  };

  const commitWeightRange = (newRange) => {
    let [min, max] = newRange;
    min = Number(min);
    max = Number(max);
    if (Number.isNaN(min) || Number.isNaN(max)) return;
    min = Math.max(min, minMaxWeight?.[0] || 0);
    max = Math.min(max, minMaxWeight?.[1] || 10000);
    if (min > max) [min, max] = [max, min];
    const clamped = [min, max];
    setLocalWeightRange(clamped);
    handleWeightRangeChange(null, clamped);
  };

  const handleRangeInputKeyDown = (e, commitFn, index, localRange) => {
    if (e.key === "Enter") {
      const newRange = [...localRange];
      newRange[index] = e.target.value;
      commitFn(newRange);
      e.target.blur();
    }
  };

  /**
   * Render attribute filter based on attribute type
   * @param {Object} attribute - Attribute data
   * @returns {JSX.Element} Attribute filter component
   */
  const renderAttributeFilter = (attribute) => {
    const attributeId = attribute.id;
    const currentValue = safeAttributeFilters[attributeId] || "";

    // Label for the attribute
    const attributeLabel = (
      <Typography variant="body2" fontWeight="medium" gutterBottom>
        {attribute.name}
      </Typography>
    );

    switch (attribute.type) {
      case "boolean":
        return (
          <Box>
            {attributeLabel}
            <FormControlLabel
              control={
                <Checkbox
                  checked={currentValue === "true"}
                  onChange={(e) =>
                    handleAttributeFilterChange(
                      attributeId,
                      e.target.checked ? "true" : ""
                    )
                  }
                />
              }
              label={t("common.yes")}
            />
          </Box>
        );

      case "select":
      case "multiselect":
        return (
          <Box>
            {attributeLabel}
            <FormControl fullWidth size="small">
              <Select
                value={currentValue}
                displayEmpty
                onChange={(e) => {
                  console.log(
                    `Select attribute ${attributeId} changed:`,
                    e.target.value
                  );
                  handleAttributeFilterChange(attributeId, e.target.value);
                }}
                sx={{ mt: 0.5 }}
              >
                <MenuItem value="">
                  <em>{tProducts("catalog.filters.allCategories")}</em>
                </MenuItem>
                {attribute.values?.map((val) => (
                  <MenuItem key={val.id} value={val.value}>
                    {val.value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        );

      case "number":
        return (
          <Box>
            {attributeLabel}
            <TextField
              type="number"
              size="small"
              fullWidth
              value={currentValue}
              onChange={(e) => {
                console.log(
                  `Number attribute ${attributeId} changed:`,
                  e.target.value
                );
                handleAttributeFilterChange(attributeId, e.target.value);
              }}
              placeholder={`${t(
                "common.enter"
              )} ${attribute.name.toLowerCase()}`}
              sx={{ mt: 0.5, "& input": { MozAppearance: "textfield", "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": { WebkitAppearance: "none", margin: 0 } } }}
            />
          </Box>
        );

      case "text":
        return (
          <Box>
            {attributeLabel}
            <TextField
              size="small"
              fullWidth
              value={currentValue}
              onChange={(e) => {
                console.log(
                  `Text attribute ${attributeId} changed:`,
                  e.target.value
                );
                handleAttributeFilterChange(attributeId, e.target.value);
              }}
              placeholder={`${t(
                "common.enter"
              )} ${attribute.name.toLowerCase()}`}
              sx={{ mt: 0.5 }}
            />
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box>
      {/* Category Filter */}
      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
        {tProducts("catalog.filters.categories")}
      </Typography>
      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
        <InputLabel id="category-select-label">
          {tProducts("catalog.filters.categories")}
        </InputLabel>
        <Select
          labelId="category-select-label"
          id="category-select"
          value={selectedCategory || ""}
          label={tProducts("catalog.filters.categories")}
          onChange={(e) => {
            console.log("Category selected in UI:", e.target.value);
            handleCategoryChange(e);
          }}
        >
          <MenuItem value="">
            {tProducts("catalog.filters.allCategories")}
          </MenuItem>
          {safeCategories
            .filter((category) => !category.parentId) // Only top-level categories
            .map((category) => (
              <MenuItem key={category.id} value={category.id}>
                {category.name}
              </MenuItem>
            ))}
        </Select>
      </FormControl>

      {/* Subcategory Filter (if category selected) */}
      {selectedCategory && subcategories.length > 0 && (
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel id="subcategory-select-label">
            {tProducts("catalog.filters.subcategories")}
          </InputLabel>
          <Select
            labelId="subcategory-select-label"
            id="subcategory-select"
            value={selectedSubcategory || ""}
            label={tProducts("catalog.filters.subcategories")}
            onChange={(e) => {
              console.log("Subcategory selected in UI:", e.target.value);
              handleSubcategoryChange(e);
            }}
          >
            <MenuItem value="">
              {tProducts("catalog.filters.allCategories")}
            </MenuItem>
            {subcategories.map((subcategory) => (
              <MenuItem key={subcategory.id} value={subcategory.id}>
                {subcategory.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      <Divider sx={{ my: 2 }} />

      {/* Price Range Filter with Applied Status Indicator */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="subtitle1"
          fontWeight="bold"
          gutterBottom
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box component="span">
            {tProducts("catalog.filters.priceRange")}
            {(priceRange[0] > minMaxPrice[0] ||
              priceRange[1] < minMaxPrice[1]) && (
              <Typography
                component="span"
                sx={{ ml: 1, fontSize: "0.8rem", color: "primary.main" }}
              >
                ({t("common.filtered")})
              </Typography>
            )}
          </Box>

          {/* Reset price filter button - only show when price filter is applied */}
          {(priceRange[0] > minMaxPrice[0] ||
            priceRange[1] < minMaxPrice[1]) && (
            <IconButton
              size="small"
              onClick={() => {
                console.log("Resetting price filter only");
                setPriceRange(minMaxPrice);
                setUserSetPriceRange(false);
              }}
              title={t("common.resetFilter")}
              sx={{ p: 0.5 }}
            >
              <RestartAltIcon fontSize="small" />
            </IconButton>
          )}
        </Typography>

        <Box sx={{ px: 1, mt: 2, mb: 2 }}>
          {/* Price range input fields above slider */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: 1,
              mb: 1,
            }}
          >
            <TextField
              type="number"
              size="small"
              value={localPriceRange[0]}
              onChange={(e) =>
                setLocalPriceRange([e.target.value, localPriceRange[1]])
              }
              onBlur={(e) =>
                commitPriceRange([e.target.value, localPriceRange[1]])
              }
              onKeyDown={(e) =>
                handleRangeInputKeyDown(e, commitPriceRange, 0, localPriceRange)
              }
              inputProps={{
                min: minMaxPrice[0] || 0,
                max: minMaxPrice[1] || 10000,
                step: 1,
              }}
              sx={{ flex: 1, "& input": { textAlign: "center", py: 0.5, MozAppearance: "textfield", "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": { WebkitAppearance: "none", margin: 0 } } }}
            />
            <TextField
              type="number"
              size="small"
              value={localPriceRange[1]}
              onChange={(e) =>
                setLocalPriceRange([localPriceRange[0], e.target.value])
              }
              onBlur={(e) =>
                commitPriceRange([localPriceRange[0], e.target.value])
              }
              onKeyDown={(e) =>
                handleRangeInputKeyDown(e, commitPriceRange, 1, localPriceRange)
              }
              inputProps={{
                min: minMaxPrice[0] || 0,
                max: minMaxPrice[1] || 10000,
                step: 1,
              }}
              sx={{ flex: 1, "& input": { textAlign: "center", py: 0.5, MozAppearance: "textfield", "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": { WebkitAppearance: "none", margin: 0 } } }}
            />
          </Box>

          {/* The price slider */}
          <Box sx={{ mt: 2, mb: 4, mx: 0 }}>
            <Slider
              value={localPriceRange}
              onChange={(e, newValue) => {
                setLocalPriceRange(newValue);
              }}
              onChangeCommitted={(e, newValue) => {
                // Only update price range when user finishes dragging
                console.log("Price slider committed:", newValue);
                handlePriceRangeChange(e, newValue);
              }}
              valueLabelDisplay="auto"
              min={minMaxPrice[0] || 0}
              max={minMaxPrice[1] || 1000}
              marks={[
                {
                  value: minMaxPrice[0] || 0,
                  label: (minMaxPrice[0] || 0).toString(),
                },
                {
                  value: minMaxPrice[1] || 1000,
                  label: (minMaxPrice[1] || 1000).toString(),
                },
              ]}
              step={Math.max(
                1,
                Math.floor(
                  ((minMaxPrice[1] || 1000) - (minMaxPrice[0] || 0)) / 100
                )
              )}
              valueLabelFormat={(value) => formatCurrency(value)}
              sx={{
                color:
                  priceRange[0] > minMaxPrice[0] ||
                  priceRange[1] < minMaxPrice[1]
                    ? "primary.main"
                    : undefined,
                "& .MuiSlider-markLabel": {
                  fontSize: "0.75rem",
                  // Move the labels inward and position them below the slider
                  top: "28px",
                  '&[data-index="0"]': {
                    left: "12px !important",
                    transform: "none",
                  },
                  '&[data-index="1"]': {
                    left: "auto !important",
                    right: "12px",
                    transform: "none",
                  },
                },
              }}
            />
          </Box>

          {/* Status indicator text below slider */}
          {(priceRange[0] > minMaxPrice[0] ||
            priceRange[1] < minMaxPrice[1]) && (
            <Typography
              variant="body2"
              color="primary.main"
              align="center"
              sx={{ mt: 1, fontSize: "0.75rem" }}
            >
              {t("common.customRangeApplied")}
            </Typography>
          )}
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Weight Range Filter */}
      {minMaxWeight && minMaxWeight[0] !== minMaxWeight[1] && (
        <>
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              gutterBottom
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box component="span">
                {tProducts("catalog.filters.weightRange")}
                {weightRange &&
                  minMaxWeight &&
                  (weightRange[0] > minMaxWeight[0] ||
                    weightRange[1] < minMaxWeight[1]) && (
                    <Typography
                      component="span"
                      sx={{ ml: 1, fontSize: "0.8rem", color: "primary.main" }}
                    >
                      ({t("common.filtered")})
                    </Typography>
                  )}
              </Box>

              {weightRange &&
                minMaxWeight &&
                (weightRange[0] > minMaxWeight[0] ||
                  weightRange[1] < minMaxWeight[1]) && (
                  <IconButton
                    size="small"
                    onClick={() => {
                      setWeightRange(minMaxWeight);
                      setUserSetWeightRange(false);
                    }}
                    title={t("common.resetFilter")}
                    sx={{ p: 0.5 }}
                  >
                    <RestartAltIcon fontSize="small" />
                  </IconButton>
                )}
            </Typography>

            <Box sx={{ px: 1, mt: 2, mb: 2 }}>
              {/* Weight range input fields above slider */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 1,
                  mb: 1,
                }}
              >
                <TextField
                  type="number"
                  size="small"
                  value={localWeightRange[0]}
                  onChange={(e) =>
                    setLocalWeightRange([e.target.value, localWeightRange[1]])
                  }
                  onBlur={(e) =>
                    commitWeightRange([e.target.value, localWeightRange[1]])
                  }
                  onKeyDown={(e) =>
                    handleRangeInputKeyDown(
                      e,
                      commitWeightRange,
                      0,
                      localWeightRange
                    )
                  }
                  inputProps={{
                    min: minMaxWeight?.[0] || 0,
                    max: minMaxWeight?.[1] || 10000,
                    step: 0.01,
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">ct</InputAdornment>
                    ),
                  }}
                  sx={{ flex: 1, "& input": { textAlign: "center", py: 0.5, MozAppearance: "textfield", "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": { WebkitAppearance: "none", margin: 0 } } }}
                />
                <TextField
                  type="number"
                  size="small"
                  value={localWeightRange[1]}
                  onChange={(e) =>
                    setLocalWeightRange([localWeightRange[0], e.target.value])
                  }
                  onBlur={(e) =>
                    commitWeightRange([localWeightRange[0], e.target.value])
                  }
                  onKeyDown={(e) =>
                    handleRangeInputKeyDown(
                      e,
                      commitWeightRange,
                      1,
                      localWeightRange
                    )
                  }
                  inputProps={{
                    min: minMaxWeight?.[0] || 0,
                    max: minMaxWeight?.[1] || 10000,
                    step: 0.01,
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">ct</InputAdornment>
                    ),
                  }}
                  sx={{ flex: 1, "& input": { textAlign: "center", py: 0.5, MozAppearance: "textfield", "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": { WebkitAppearance: "none", margin: 0 } } }}
                />
              </Box>

              <Box sx={{ mt: 2, mb: 4, mx: 0 }}>
                <Slider
                  value={localWeightRange}
                  onChange={(e, newValue) => {
                    setLocalWeightRange(newValue);
                  }}
                  onChangeCommitted={(e, newValue) => {
                    handleWeightRangeChange(e, newValue);
                  }}
                  valueLabelDisplay="auto"
                  min={minMaxWeight[0] || 0}
                  max={minMaxWeight[1] || 1000}
                  marks={[
                    {
                      value: minMaxWeight[0] || 0,
                      label: (minMaxWeight[0] || 0).toString(),
                    },
                    {
                      value: minMaxWeight[1] || 1000,
                      label: (minMaxWeight[1] || 1000).toString(),
                    },
                  ]}
                  step={Math.max(
                    0.01,
                    Math.floor(
                      ((minMaxWeight[1] || 1000) - (minMaxWeight[0] || 0)) / 100 * 100
                    ) / 100
                  )}
                  valueLabelFormat={(value) => `${value} ct`}
                  sx={{
                    color:
                      weightRange &&
                      minMaxWeight &&
                      (weightRange[0] > minMaxWeight[0] ||
                        weightRange[1] < minMaxWeight[1])
                        ? "primary.main"
                        : undefined,
                    "& .MuiSlider-markLabel": {
                      fontSize: "0.75rem",
                      top: "28px",
                      '&[data-index="0"]': {
                        left: "12px !important",
                        transform: "none",
                      },
                      '&[data-index="1"]': {
                        left: "auto !important",
                        right: "12px",
                        transform: "none",
                      },
                    },
                  }}
                />
              </Box>

              {weightRange &&
                minMaxWeight &&
                (weightRange[0] > minMaxWeight[0] ||
                  weightRange[1] < minMaxWeight[1]) && (
                  <Typography
                    variant="body2"
                    color="primary.main"
                    align="center"
                    sx={{ mt: 1, fontSize: "0.75rem" }}
                  >
                    {t("common.customRangeApplied")}
                  </Typography>
                )}
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />
        </>
      )}

      {/* Attribute Filters - Show attributes directly when category is selected */}
      {(selectedCategory || selectedSubcategory) && (
        <>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            {tProducts("catalog.filters.attributes")}
            {Object.keys(safeAttributeFilters).filter(
              (key) => safeAttributeFilters[key]
            ).length > 0 && (
              <Typography
                component="span"
                sx={{ ml: 1, fontSize: "0.8rem", color: "primary.main" }}
              >
                (
                {
                  Object.keys(safeAttributeFilters).filter(
                    (key) => safeAttributeFilters[key]
                  ).length
                }{" "}
                {t("common.active")})
              </Typography>
            )}
          </Typography>

          {loadingAttributes ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : safeAvailableAttributes.length > 0 ? (
            safeAvailableAttributes.map((attribute) => (
              <Box key={attribute.id} sx={{ mb: 2 }}>
                {renderAttributeFilter(attribute)}
              </Box>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              {tProducts("catalog.filters.noAttributes")}
            </Typography>
          )}

          <Divider sx={{ my: 2 }} />
        </>
      )}

      {/* Clear Filters Button */}
      <Button
        variant="outlined"
        fullWidth
        onClick={() => {
          console.log("Clear filters button clicked");
          clearFilters();
        }}
        sx={{ mb: 2 }}
      >
        {tProducts("catalog.filters.clear")}
      </Button>
    </Box>
  );
};

export default ProductFilters;
